import { useEffect, useRef } from "react";
import { useIsMobile } from "@/components/ui/use-mobile";

/* Motion, in three layers, so the field reads as alive without the clustered
   composition dispersing or the headline losing legibility:
   - WANDER: each star weaves around its seeded home on a small ellipse, so
     neighbours move relative to each other and the connecting lines gently flex.
     This is the motion the eye actually notices. A rigid whole-field drift alone
     is nearly invisible, because nothing moves relative to anything else.
   - SWAY: a slow collective translate for a touch of global parallax.
   - BEAM (in paint): the tinted colour spots swing their brightness and halo
     size far more than the base ink dots, pulsing like a slow beacon.
   All bounded, so nothing drifts out of place; none of it runs under
   prefers-reduced-motion (that path paints one static frame and stops). */
const WANDER_AMP = 28; // px, scaled by per-point depth
const WANDER_RATE = 0.00038; // rad/ms, ~16.5s base period
const SWAY_AMP_X = 20; // px
const SWAY_AMP_Y = 13; // px
const SWAY_FREQ_X = 0.00014; // rad/ms, ~45s period
const SWAY_FREQ_Y = 0.00011; // rad/ms, ~57s period
const SWAY_PHASE_Y = 1.1;
const CLEAR_MARGIN = 56; // px, covers wander + sway so the redraw never smears

interface ConstellationProps {
  /** Ink color for dots and lines, as `r, g, b`. Defaults to foreground ink (#0F172A). */
  inkRgb?: string;
  /** Multiplier for line alpha. Default 0.25. */
  lineAlpha?: number;
  /** Multiplier for dot alpha. Default 0.55. */
  dotAlpha?: number;
  /** Apply the soft radial mask so dots/lines fade at the edges. Default true. */
  masked?: boolean;
  /**
   * Seed points in clusters rather than uniformly at random, so the field
   * reads as actual constellations instead of even noise. Uniform seeding has
   * no focal point and competes with foreground type at every pixel.
   * Default false, which preserves the original look for existing callers.
   */
  clustered?: boolean;
  /**
   * Accent token names (e.g. ["violet", "sky"]) used to tint a minority of
   * nodes. Values are read from the CSS custom properties at runtime, so the
   * theme stays the single source of truth. Empty means monochrome.
   */
  tints?: string[];
  /** Fraction of nodes that receive a tint. Default 0.25. */
  tintRatio?: number;
  /** Extra mask layer (a CSS image); transparent there = canvas cleared.
      Intersected with the base mask, so it only subtracts (hero investor line). */
  clearMask?: string;
  className?: string;
  style?: React.CSSProperties;
}

type Point = {
  x: number;
  y: number;
  /** Seeded home position. The point weaves around this and never drifts off. */
  x0: number;
  y0: number;
  z: number;
  /** Wander phase. */
  wob: number;
  r: number;
  /** Beam / twinkle phase. */
  twk: number;
  /** Resolved "H S% L%" channel triplet, or null for the base ink. */
  tint: string | null;
};

export function Constellation({
  inkRgb = "15,23,42",
  lineAlpha = 0.25,
  dotAlpha = 0.55,
  masked = true,
  clustered = false,
  tints = [],
  tintRatio = 0.25,
  clearMask,
  className,
  style,
}: ConstellationProps) {
  /* Arrays are a fresh identity each render, so the effect depends on a
     stable serialisation rather than the array itself. */
  const tintKey = tints.join(",");
  const canvasRef = useRef<HTMLCanvasElement>(null);
  // Cross-cutting layout signal; drives mobile-only work reduction (lower node
  // density, rAF paused while off-screen). Desktop path is unchanged.
  const isMobile = useIsMobile();

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    /* prefers-reduced-motion REDUCES the positional motion (wander + sway), it
       does not freeze it: this is a small, slow ambient background drift, not
       parallax-on-scroll or anything that reads as a vestibular trigger, so it
       scales down rather than stopping. The brightness beam is not spatial
       motion at all and always runs at full strength. */
    const reduceMotion =
      typeof window !== "undefined" &&
      window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    const motionScale = reduceMotion ? 0.7 : 1;

    const dpr = Math.min(window.devicePixelRatio || 1, 2);
    let w = 0;
    let h = 0;
    let pts: Point[] = [];
    let rafId = 0;

    function resize() {
      if (!canvas || !ctx) return;
      w = canvas.clientWidth;
      h = canvas.clientHeight;
      canvas.width = w * dpr;
      canvas.height = h * dpr;
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    }

    /* Resolve the accent tokens to raw "H S% L%" triplets once. Canvas cannot
       read CSS custom properties, so var() references have to be flattened
       here rather than hardcoded, keeping theme.css authoritative. */
    const tintChannels: string[] = tintKey
      ? tintKey
          .split(",")
          .map((name) =>
            getComputedStyle(document.documentElement)
              .getPropertyValue(`--accent-${name}-ink`)
              .trim(),
          )
          .filter(Boolean)
      : [];

    function pickTint(): string | null {
      if (!tintChannels.length || Math.random() >= tintRatio) return null;
      return tintChannels[Math.floor(Math.random() * tintChannels.length)];
    }

    function seed() {
      // Mobile: thin the field out (cap + larger divisor) so the per-frame
      // O(n^2) line pass stays cheap. Desktop keeps the original 140 / 9000.
      const cap = isMobile ? 64 : 140;
      const divisor = isMobile ? 16000 : 9000;
      pts = [];

      if (clustered) {
        /* Clustered: fewer nodes, grouped, so recognisable constellations form
           and the eye gets somewhere to rest. Halving the count also quarters
           the O(n^2) line pass, so this is cheaper than uniform seeding.

           Cluster centres are distributed on a RING rather than uniformly.
           Uniform seeding drops clusters directly behind the headline, which
           crowds the optical centre while leaving the lower band empty. The
           ring keeps the middle clear for type and spreads weight outward and
           downward. Each cluster gets an evenly spaced angle plus jitter, so
           they never bunch the way pure random does. */
        const groups = isMobile ? 4 : 7;
        const per = isMobile ? 7 : 10;
        const TAU = Math.PI * 2;
        for (let c = 0; c < groups; c++) {
          const ang = (c / groups) * TAU + Math.random() * (TAU / groups);
          const rr = 0.46 + Math.random() * 0.32;
          const cx = w * 0.5 + Math.cos(ang) * rr * w * 0.6;
          /* Ring centred slightly below the midline, with more vertical spread
             than horizontal, so the empty lower band fills in. */
          const cy = h * 0.52 + Math.sin(ang) * rr * h * 0.68;
          for (let i = 0; i < per; i++) {
            const pang = Math.random() * Math.PI * 2;
            // pow < 1 biases toward the cluster centre without hard packing
            const rad = Math.pow(Math.random(), 0.7) * (isMobile ? 110 : 150);
            const px = cx + Math.cos(pang) * rad;
            const py = cy + Math.sin(pang) * rad;
            pts.push({
              x: px,
              y: py,
              x0: px,
              y0: py,
              z: 0.45 + Math.random() * 0.55,
              wob: Math.random() * Math.PI * 2,
              r: 1.1 + Math.random() * 2.4,
              twk: Math.random() * Math.PI * 2,
              tint: pickTint(),
            });
          }
        }
        return;
      }

      const density = Math.min(cap, Math.floor((w * h) / divisor));
      for (let i = 0; i < density; i++) {
        const px = Math.random() * w;
        const py = Math.random() * h;
        pts.push({
          x: px,
          y: py,
          x0: px,
          y0: py,
          z: 0.35 + Math.random() * 0.65,
          wob: Math.random() * Math.PI * 2,
          r: 0.8 + Math.random() * 2,
          twk: Math.random() * Math.PI * 2,
          tint: pickTint(),
        });
      }
    }

    function paint(t: number) {
      if (!ctx) return;
      /* Clear beyond the bounds so wander + sway never leave a smear at the
         edges. */
      ctx.clearRect(
        -CLEAR_MARGIN,
        -CLEAR_MARGIN,
        w + CLEAR_MARGIN * 2,
        h + CLEAR_MARGIN * 2,
      );

      /* Collective sway: shift the whole field a few pixels along a slow
         Lissajous. Rigid translate, so clusters and lines keep their shape. */
      const swayX = Math.sin(t * SWAY_FREQ_X) * SWAY_AMP_X * motionScale;
      const swayY =
        Math.sin(t * SWAY_FREQ_Y + SWAY_PHASE_Y) * SWAY_AMP_Y * motionScale;
      ctx.save();
      ctx.translate(swayX, swayY);

      /* Shorter link distance when clustered, so lines connect within a
         constellation rather than smearing the whole field into one mesh. */
      const maxDist = clustered ? 135 : 170;
      for (let i = 0; i < pts.length; i++) {
        const a = pts[i];
        for (let j = i + 1; j < pts.length; j++) {
          const b = pts[j];
          const dx = a.x - b.x;
          const dy = a.y - b.y;
          const d2 = dx * dx + dy * dy;
          if (d2 < maxDist * maxDist) {
            const d = Math.sqrt(d2);
            const alpha = (1 - d / maxDist) * lineAlpha * Math.min(a.z, b.z);
            ctx.strokeStyle = `rgba(${inkRgb},${alpha})`;
            ctx.lineWidth = 0.7;
            ctx.beginPath();
            ctx.moveTo(a.x, a.y);
            ctx.lineTo(b.x, b.y);
            ctx.stroke();
          }
        }
      }

      /* Clustered fields carry a wider halo, since there are fewer nodes each
         doing more work. */
      const haloScale = clustered ? 6 : 4;
      const haloAlpha = clustered ? 0.3 : 0.15;

      for (const p of pts) {
        const tinted = p.tint !== null;
        /* Beam: the colour spots swing their brightness and halo size far more
           than the base ink dots, so they pulse like a slow beacon rather than a
           faint twinkle. `s` is a 0..1 breath; `floor` is how dim the trough
           goes (lower for the colour spots, so they beam harder). */
        const pulseRate = tinted ? 0.0013 : 0.0016;
        const s = 0.5 + 0.5 * Math.sin(t * pulseRate + p.twk);
        const floor = tinted ? 0.25 : 0.6;
        const beam = floor + (1 - floor) * s;
        const alpha = dotAlpha * p.z * beam;
        /* Tinted nodes use the resolved token channels; the rest use base ink. */
        const solid = p.tint
          ? `hsl(${p.tint} / ${alpha})`
          : `rgba(${inkRgb},${alpha})`;
        const fade = p.tint ? `hsl(${p.tint} / 0)` : `rgba(${inkRgb},0)`;
        const halo = p.tint
          ? `hsl(${p.tint} / ${alpha * haloAlpha})`
          : `rgba(${inkRgb},${alpha * haloAlpha})`;

        ctx.fillStyle = solid;
        ctx.beginPath();
        ctx.arc(p.x, p.y, p.r * p.z, 0, Math.PI * 2);
        ctx.fill();

        /* The colour halos swell and shrink with the beam; base halos hold. */
        const rad = p.r * p.z * haloScale * (tinted ? 0.8 + 0.6 * s : 1);
        const g = ctx.createRadialGradient(p.x, p.y, 0, p.x, p.y, rad);
        g.addColorStop(0, halo);
        g.addColorStop(1, fade);
        ctx.fillStyle = g;
        ctx.beginPath();
        ctx.arc(p.x, p.y, rad, 0, Math.PI * 2);
        ctx.fill();
      }

      ctx.restore();
    }

    function frame(t: number) {
      if (!ctx) return;
      for (const p of pts) {
        /* Weave each star around its home so neighbours move relative to each
           other (what makes the field read as moving), with depth parallax.
           Bounded to WANDER_AMP, so the clustered composition never disperses. */
        p.x =
          p.x0 +
          Math.sin(t * WANDER_RATE + p.wob) * WANDER_AMP * p.z * motionScale;
        p.y =
          p.y0 +
          Math.sin(t * WANDER_RATE * 1.3 + p.wob * 1.7) *
            WANDER_AMP *
            p.z *
            motionScale;
      }
      paint(t);
      rafId = requestAnimationFrame(frame);
    }

    function handleResize() {
      resize();
      seed();
    }

    resize();
    seed();
    window.addEventListener("resize", handleResize);

    // Mobile-only: pause the rAF while the canvas is scrolled off-screen so a
    // background that no one can see does not burn battery. Desktop keeps the
    // always-on loop (behavior identical).
    let io: IntersectionObserver | null = null;
    const start = () => {
      if (rafId) return;
      rafId = requestAnimationFrame(frame);
    };
    const stop = () => {
      if (rafId) cancelAnimationFrame(rafId);
      rafId = 0;
    };

    if (isMobile && typeof IntersectionObserver !== "undefined") {
      io = new IntersectionObserver(
        (entries) => {
          if (entries.some((e) => e.isIntersecting)) start();
          else stop();
        },
        { rootMargin: "100px 0px" },
      );
      io.observe(canvas);
    } else {
      start();
    }

    return () => {
      stop();
      window.removeEventListener("resize", handleResize);
      if (io) io.disconnect();
    };
  }, [inkRgb, lineAlpha, dotAlpha, isMobile, clustered, tintKey, tintRatio]);

  /* Two different masks, because "framing the content" and "getting out of the
     way of the content" are opposite gradients.

     Default (edge vignette): opaque in the middle, fading out at the edges.
     Correct where the canvas is decoration behind a short block of text.

     Clustered (centre clearing): TRANSPARENT in the middle, ramping to full
     density outward. Used where the canvas sits behind a large headline. The
     edge vignette put peak density directly behind the type, which is what
     made the optical centre feel crowded while the lower band read as empty. */
  const maskImage = clustered
    ? "radial-gradient(ellipse 66% 56% at 50% 42%, transparent 20%, black 74%)"
    : "radial-gradient(ellipse 90% 70% at 50% 45%, black 30%, transparent 90%)";

  /* Clear zone intersected with the base mask, so it only ever subtracts.
     Safari needs the prefixed composite keyword. */
  const layers = [masked ? maskImage : "", clearMask ?? ""].filter(Boolean);
  const mask = layers.join(", ");
  const composite = layers.length > 1 ? "intersect" : undefined;

  return (
    <canvas
      ref={canvasRef}
      aria-hidden
      className={className}
      style={{
        position: "absolute",
        inset: 0,
        width: "100%",
        height: "100%",
        pointerEvents: "none",
        WebkitMaskImage: mask,
        maskImage: mask,
        WebkitMaskComposite: composite && "source-in",
        maskComposite: composite,
        ...style,
      }}
    />
  );
}
